import {
  Button,
  ButtonVariant,
  FormMessage,
  Input,
  InputGroup,
  Spinner,
} from '@aus-platform/design-system';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  handleFormInputMessage,
  useChangeLoggedUserPasswordRequest,
  useLoggedUser,
  useUpdateUserRequest,
} from 'shared/api';
import { UserLocalDataManager } from 'shared/helpers';
import { useInputFields } from 'shared/hooks';
import { PasswordValidationMessage } from 'src/users/components';
import type {
  ChangePasswordInput,
  ProfileTabProps,
  UpdateUserInput,
} from '../types';

const updateUserInputState: UpdateUserInput = {
  firstName: '',
  lastName: '',
  email: '',
};

const changePasswordInputState: ChangePasswordInput = {
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: '',
};

export const ProfileTab: React.FC<ProfileTabProps> = ({ onClose }) => {
  // States.
  const [isPasswordChanged, setIsPasswordChanged] = useState(false);
  const [formError, setFormError] = useState<string>('');
  const [isPasswordInvalid, setIsPasswordInvalid] = useState(false);

  // Hooks.
  const {
    values,
    names,
    errors,
    setValues,
    setErrors,
    onChange,
    onBlur,
    onFocus,
    inputHasError,
    inputIsDirty,
  } = useInputFields<UpdateUserInput>(updateUserInputState);

  const {
    values: changePasswordInputValues,
    names: changePasswordInputNames,
    errors: changePasswordInputError,
    setValues: setChangePasswordInputValues,
    setErrors: setChangePasswordInputErrors,
    onChange: onChangePasswordInputChange,
    onBlur: onChangePasswordInputBlur,
    onFocus: onFocusPasswordInput,
    inputHasError: changePasswordInputFieldsHasError,
    inputIsDirty: changePasswordInputIsDirty,
    resetAll: resetChangePasswordInput,
  } = useInputFields<ChangePasswordInput>(changePasswordInputState);

  // Variables.
  const isInputDirty = inputIsDirty() || changePasswordInputIsDirty();
  const hasChangePasswordErrors =
    isPasswordChanged &&
    (changePasswordInputFieldsHasError() || isPasswordInvalid);

  const isSubmitButtonDisabled =
    inputHasError() || !isInputDirty || hasChangePasswordErrors;

  // Apis.
  const {
    data: user,
    refetch: refetchUserDetails,
    isSuccess: userDetailsIsSuccess,
    isLoading: userDetailsIsLoading,
  } = useLoggedUser();

  const {
    mutate: sendUpdateUserRequest,
    data: updateUserResponse,
    isError: isErrorUpdateUser,
    isPending: isLoadingUpdateUser,
    isSuccess: isSuccessUpdateUser,
    error: updateUserError,
  } = useUpdateUserRequest();

  const {
    mutate: sendChangePasswordRequest,
    data: changePasswordResponse,
    isError: changePasswordRequestIsError,
    isPending: changePasswordRequestIsLoading,
    isSuccess: changePasswordRequestIsSuccess,
    error: changePasswordRequestError,
  } = useChangeLoggedUserPasswordRequest();

  // UseEffects.
  useEffect(() => {
    refetchUserDetails();
    setIsPasswordChanged(false);
  }, []);

  useEffect(() => {
    if (userDetailsIsSuccess && user) {
      setValues({
        firstName: user.data.firstName,
        lastName: user.data.lastName,
        email: user.data.email,
      });
    }
  }, [userDetailsIsSuccess, user]);

  useEffect(() => {
    if (isSuccessUpdateUser && updateUserResponse) {
      const userName = `${updateUserResponse.data.firstName} ${updateUserResponse.data.lastName}`;
      UserLocalDataManager.saveUserName(userName);
      onClose();
    }
    handleFormInputMessage(
      isSuccessUpdateUser,
      isErrorUpdateUser,
      updateUserResponse,
      updateUserError,
      setErrors,
      setFormError,
    );
  }, [isSuccessUpdateUser, isErrorUpdateUser]);

  useEffect(() => {
    if (changePasswordRequestIsSuccess && changePasswordResponse) {
      setChangePasswordInputValues(changePasswordInputState);
      setIsPasswordChanged(false);
      onClose();
    }
    handleFormInputMessage(
      changePasswordRequestIsSuccess,
      changePasswordRequestIsError,
      changePasswordResponse,
      changePasswordRequestError,
      setChangePasswordInputErrors,
      setFormError,
    );
  }, [changePasswordRequestIsSuccess, changePasswordRequestIsError]);

  // Handlers.
  const onSubmit = (e) => {
    e.preventDefault();
    if (!inputHasError() && inputIsDirty()) {
      sendUpdateUserRequest({
        ...values,
        id: user?.data.id,
      });
    }

    if (isPasswordChanged) {
      if (
        !changePasswordInputFieldsHasError() &&
        changePasswordInputIsDirty()
      ) {
        sendChangePasswordRequest({
          ...changePasswordInputValues,
        });
      }
    }
  };

  const handleToggleChangePassword = () => {
    setIsPasswordChanged(!isPasswordChanged);
    resetChangePasswordInput();

    if (formError === 'Incorrect old password') {
      setFormError('');
    }
  };

  const onChangePasswordInput = (event) => {
    if (!isEmpty(formError)) {
      setFormError('');
    }
    onChangePasswordInputChange(event);

    const isConfirmNewPasswordMismatch =
      !isEmpty(changePasswordInputValues.confirmNewPassword) &&
      event.target.value !== changePasswordInputValues.confirmNewPassword;

    setChangePasswordInputErrors((errors) => ({
      ...errors,
      confirmNewPassword: isConfirmNewPasswordMismatch
        ? "Passwords don't match."
        : '',
    }));
  };

  const onBlurPasswordInput = (event: React.FocusEvent<HTMLInputElement>) => {
    onChangePasswordInputBlur(event);

    if (!isEmpty(changePasswordInputValues.newPassword)) {
      setChangePasswordInputErrors((prev) => ({
        ...prev,
        newPassword: isPasswordInvalid ? ' ' : '',
      }));
    }
  };

  if (userDetailsIsLoading) {
    return <Spinner />;
  }

  return (
    <div className="settings-content">
      <form
        id="profile-tab-form"
        className="settings-content__body profile-tab"
        onSubmit={onSubmit}
      >
        <div className="profile-tab__name-container">
          <InputGroup>
            <Input.Label>First Name</Input.Label>
            <Input.Text
              placeholder="First Name"
              value={values.firstName}
              name={names.firstName}
              error={errors.firstName}
              isInvalid={!!errors.firstName}
              {...{ onChange, onBlur, onFocus }}
            />
          </InputGroup>
          <InputGroup>
            <Input.Label>Last Name</Input.Label>
            <Input.Text
              placeholder="Last Name"
              value={values.lastName}
              name={names.lastName}
              error={errors.lastName}
              isInvalid={!!errors.lastName}
              {...{ onChange, onBlur, onFocus }}
            />
          </InputGroup>
        </div>
        <InputGroup>
          <Input.Label>Email</Input.Label>
          <Input.Text
            value={values.email}
            name={names.email}
            error={errors.email}
            isInvalid={!!errors.email}
            disabled
            {...{ onChange, onBlur, onFocus }}
          />
        </InputGroup>
        <div className="profile-tab__change-password">
          <Button
            className="profile-tab__change-password__btn"
            variant={
              isPasswordChanged ? ButtonVariant.DangerLink : ButtonVariant.Link
            }
            onClick={handleToggleChangePassword}
          >
            {isPasswordChanged ? 'Cancel' : 'Change Password'}
          </Button>

          {isPasswordChanged && (
            <div className="profile-tab__change-password__form">
              <InputGroup>
                <Input.Label>Current Password</Input.Label>
                <Input.Password
                  placeholder="Current Password"
                  value={changePasswordInputValues.currentPassword}
                  name={changePasswordInputNames.currentPassword}
                  error={changePasswordInputError.currentPassword}
                  isInvalid={!!changePasswordInputError.currentPassword}
                  autoComplete="current-password"
                  onChange={onChangePasswordInputChange}
                  onBlur={onChangePasswordInputBlur}
                  onFocus={onFocusPasswordInput}
                />
              </InputGroup>
              <InputGroup>
                <Input.Label>New Password</Input.Label>
                <Input.Password
                  placeholder="New Password"
                  value={changePasswordInputValues.newPassword}
                  name={changePasswordInputNames.newPassword}
                  error={changePasswordInputError.newPassword}
                  isInvalid={!!changePasswordInputError.newPassword}
                  autoComplete="new-password"
                  onChange={onChangePasswordInput}
                  onBlur={onBlurPasswordInput}
                  onFocus={onFocusPasswordInput}
                />
              </InputGroup>

              <PasswordValidationMessage
                password={changePasswordInputValues.newPassword}
                setIsPasswordValidationError={setIsPasswordInvalid}
              />

              <InputGroup>
                <Input.Label>Confirm New Password</Input.Label>
                <Input.Password
                  placeholder="Confirm New Password"
                  value={changePasswordInputValues.confirmNewPassword}
                  name={changePasswordInputNames.confirmNewPassword}
                  error={changePasswordInputError.confirmNewPassword}
                  isInvalid={!!changePasswordInputError.confirmNewPassword}
                  onChange={onChangePasswordInputChange}
                  onBlur={onChangePasswordInputBlur}
                  onFocus={onFocusPasswordInput}
                />
              </InputGroup>
            </div>
          )}
        </div>
        {!isEmpty(formError) && <FormMessage message={formError} />}
      </form>

      <div className="settings-content__footer">
        <Button
          type="submit"
          form="profile-tab-form"
          isLoading={isLoadingUpdateUser || changePasswordRequestIsLoading}
          disabled={isSubmitButtonDisabled}
        >
          Update Details
        </Button>
      </div>
    </div>
  );
};
